import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@org/ui';

export type ReportColumn<T> = {
  key: string;
  label: string;
  /** Value used for sorting (and, when `render` is absent, for display). */
  value: (row: T) => string | number | null | undefined;
  render?: (row: T) => React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
};

type ReportTableProps<T> = {
  rows: T[];
  columns: ReportColumn<T>[];
  rowKey: (row: T) => string;
  /** Column key to sort by initially (descending). */
  defaultSort?: string;
  defaultDirection?: 'asc' | 'desc';
  /** Rows shown before the "show more" button. */
  pageSize?: number;
  onRowClick?: (row: T) => void;
  /** Free-text filter over the given column keys. */
  searchKeys?: string[];
  searchPlaceholder?: string;
};

/**
 * A light sortable table for report rows. It is plain HTML on purpose: the
 * reports are read-only, and the numbers must stay copy-pasteable.
 */
export function ReportTable<T>({
  rows,
  columns,
  rowKey,
  defaultSort,
  defaultDirection = 'desc',
  pageSize = 25,
  onRowClick,
  searchKeys,
  searchPlaceholder,
}: ReportTableProps<T>) {
  const { t } = useTranslation('admin');
  const [sortKey, setSortKey] = React.useState<string | undefined>(defaultSort);
  const [direction, setDirection] = React.useState<'asc' | 'desc'>(defaultDirection);
  const [limit, setLimit] = React.useState(pageSize);
  const [query, setQuery] = React.useState('');

  const filtered = React.useMemo(() => {
    if (!query.trim() || !searchKeys?.length) return rows;
    const needle = query.trim().toLowerCase();
    const cols = columns.filter((c) => searchKeys.includes(c.key));
    return rows.filter((row) =>
      cols.some((c) => String(c.value(row) ?? '').toLowerCase().includes(needle)),
    );
  }, [rows, query, searchKeys, columns]);

  const sorted = React.useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return filtered;
    const sign = direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = col.value(a);
      const vb = col.value(b);
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
      return String(va).localeCompare(String(vb)) * sign;
    });
  }, [filtered, columns, sortKey, direction]);

  const visible = sorted.slice(0, limit);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setDirection('desc');
    }
  };

  return (
    <div className="space-y-3">
      {searchKeys?.length ? (
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setLimit(pageSize);
          }}
          placeholder={searchPlaceholder ?? t('analytics.common.search')}
          className="h-9 w-full max-w-xs rounded-md border bg-background px-3 text-sm"
        />
      ) : null}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              {columns.map((c) => {
                const active = sortKey === c.key;
                const Icon = !active ? ArrowUpDown : direction === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={c.key}
                    className={cn(
                      'whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground',
                      c.align === 'right' && 'text-right',
                      c.className,
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(c.key)}
                      className={cn('inline-flex items-center gap-1', c.align === 'right' && 'flex-row-reverse')}
                    >
                      {c.label}
                      <Icon className={cn('h-3 w-3', active ? 'text-foreground' : 'text-muted-foreground/60')} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  {t('analytics.common.noData')}
                </td>
              </tr>
            ) : (
              visible.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={cn('border-b last:border-0', onRowClick && 'cursor-pointer hover:bg-muted/40')}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        'px-3 py-2 align-middle',
                        c.align === 'right' && 'text-right tabular-nums',
                        c.className,
                      )}
                    >
                      {c.render ? c.render(row) : (c.value(row) ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{t('analytics.common.showingOf', { shown: visible.length, total: sorted.length })}</span>
        {sorted.length > limit ? (
          <button
            type="button"
            onClick={() => setLimit((l) => l + pageSize)}
            className="rounded-md border px-2 py-1 hover:bg-muted"
          >
            {t('analytics.common.showMore')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
